//admin layout 
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import {
  Menu,
  Film,
  Package,
  LogOut,
  Store,
  ShoppingBag,
  LayoutDashboard,
  FileText,
  Image as ImageIcon,
  Star,
  Ticket,
  Sparkles,
  BellRing,
  Gem,
  TrendingUp
  ,UsersRound
} from 'lucide-react';
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import auraLogo from '@/assets/uma-aura-logo.svg';
import { cn } from '@/lib/utils';
import { moduleForPath } from '@/lib/adminAccess';
import { AdminPermissionProvider } from '@/contexts/AdminPermissionContext';
import { adminModuleForPath } from '@/lib/adminPageModules';

const AdminShellContext = createContext(false);

// Existing admin pages still wrap their content in AdminLayout. Inside the
// persistent route shell those wrappers only render their page content.
export const AdminLayout = (props: { children: React.ReactNode; independentScroll?: boolean }) => {
  const insideShell = useContext(AdminShellContext);
  return insideShell ? <>{props.children}</> : <AdminLayoutFrame {...props} />;
};

const AdminLayoutFrame = ({ children, independentScroll = false }: { children: React.ReactNode; independentScroll?: boolean }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userPhone, logout, adminAccess, canAdmin } = useAuth();
  const readOnly = adminAccess?.kind === 'staff' && !adminAccess.permissions.some(permission => !permission.endsWith('.view'));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigationRequest = useRef(0);

  const navItems = [
    { name: 'Dashboard', href: '/admin/dashboard', icon: LayoutDashboard, visible: true },
    { name: 'Order notifications', href: '/admin/notifications', icon: BellRing, visible: true },
    { name: 'Shipping & delivery', href: '/admin/shipping', icon: Package, visible: true },
    { name: 'Orders', href: '/admin/orders', icon: ShoppingBag, visible: true },
    { name: 'Follow-ups', href: '/admin/followups', icon: UsersRound, visible: true },
    { name: 'Gemfinder', href: '/admin/gemfinder', icon: Gem, visible: true },
    { name: 'Products', href: '/admin/products', icon: Package, visible: true },
    { name: 'Inventory', href: '/admin/inventory', icon: Package, visible: true },
    { name: 'Bestsellers', href: '/admin/bestsellers', icon: TrendingUp, visible: true },
    { name: 'Offers', href: '/admin/offers', icon: Sparkles, visible: true },
    { name: 'Bracelet Gifts', href: '/admin/bracelet-gift', icon: Sparkles, visible: true },
    { name: 'Blogs', href: '/admin/blogs', icon: FileText, visible: true },
    { name: 'Social videos', href: '/admin/videos', icon: Film, visible: true },
    { name: 'Banners', href: '/admin/banners', icon: ImageIcon, visible: true },
    { name: 'App Promotion', href: '/admin/app-promotion', icon: Sparkles, visible: true },
    { name: 'Reviews', href: '/admin/reviews', icon: Star, visible: true },
    { name: 'Coupons', href: '/admin/coupons', icon: Ticket, visible: true },
    { name: 'Recent Purchases', href: '/admin/recent-purchases', icon: BellRing, visible: true },
    { name: 'Team & roles', href: '/admin/access', icon: UsersRound, visible: adminAccess?.isSuperAdmin === true },
  ];

  const availablePaths = navItems
    .filter(item => item.visible && (item.href === '/admin/access' || canAdmin(`${moduleForPath(item.href)}.view`)))
    .map(item => item.href);
  const availablePathsKey = availablePaths.join('|');

  // Warm permitted pages after the current page has settled. Loading one at a
  // time avoids competing with the active module's requests on mobile networks.
  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;
    let idle: number | undefined;
    let index = 0;
    const paths = availablePathsKey.split('|').filter(path => path !== location.pathname && adminModuleForPath[path]);
    const loadNext = () => {
      if (cancelled || index >= paths.length) return;
      void adminModuleForPath[paths[index++]]().catch(() => undefined).finally(() => {
        if (!cancelled) timer = window.setTimeout(loadNext, 300);
      });
    };
    if (typeof window.requestIdleCallback === 'function') {
      idle = window.requestIdleCallback(loadNext, { timeout: 2000 });
    } else {
      timer = window.setTimeout(loadNext, 1200);
    }
    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
      if (idle !== undefined) window.cancelIdleCallback(idle);
    };
  }, [availablePathsKey, location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const NavLinks = () => (
    <>
      {navItems.filter(item => item.visible && (item.href === '/admin/access' || canAdmin(`${moduleForPath(item.href)}.view`))).map((item) => {
        const Icon = item.icon;
        const isActive = location.pathname.startsWith(item.href);
        return (
          <Link
            key={item.name}
            to={item.href}
            onPointerEnter={() => { void adminModuleForPath[item.href]?.().catch(() => undefined); }}
            onFocus={() => { void adminModuleForPath[item.href]?.().catch(() => undefined); }}
            onTouchStart={() => { void adminModuleForPath[item.href]?.().catch(() => undefined); }}
            onClick={event => {
              setMobileMenuOpen(false);
              if (isActive || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
              event.preventDefault();
              const request = ++navigationRequest.current;
              void adminModuleForPath[item.href]().then(() => {
                if (navigationRequest.current === request) navigate(item.href);
              }).catch(() => {
                if (navigationRequest.current === request) toast.error('This admin page could not load. Please try again.');
              });
            }}
            className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              isActive 
                ? 'bg-primary text-primary-foreground font-medium' 
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            }`}
          >
            <Icon className="h-5 w-5" />
            <span>{item.name}</span>
          </Link>
        );
      })}
    </>
  );

  return (
    <AdminShellContext.Provider value={true}><AdminPermissionProvider value={canAdmin}><div className={cn('admin-workspace bg-slate-50', independentScroll ? 'flex h-dvh flex-col overflow-hidden' : 'min-h-screen')}>
      {/* Header */}
      <header className={cn('sticky top-0 z-50 bg-white border-b border-border shadow-sm', independentScroll && 'shrink-0')}>
        <div className="container mx-auto px-4">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Mobile Menu Toggle */}
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="lg:hidden">
                    <Menu className="h-6 w-6" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[280px] overflow-y-auto bg-background">
                  <div className="flex flex-col gap-4 mt-6">
                    <NavLinks />
                    <div className="pt-4 border-t border-border">
                      <Button
                        variant="ghost"
                        className="w-full justify-start gap-3 text-muted-foreground hover:text-foreground"
                        onClick={() => {
                          setMobileMenuOpen(false);
                          navigate('/');
                        }}
                      >
                        <Store className="h-5 w-5" />
                        <span>Back to Store</span>
                      </Button>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>

              {/* Logo */}
              <Link to="/" className="flex items-center gap-2">
                <img src={auraLogo} alt="Uma's Aura" className="h-8 w-auto" />
              </Link>

              {/* Admin Badge */}
              <Badge variant="default" className="hidden sm:inline-flex">
                {adminAccess?.isSuperAdmin ? 'Super Admin' : adminAccess?.roleName || 'Admin Panel'}
              </Badge>
            </div>

            {/* User Info & Actions */}
            <div className="flex items-center gap-4">
              <div className="hidden md:flex items-center gap-2 text-sm text-muted-foreground">
                <span>+91 {userPhone}</span>
              </div>
              
              <Button
                variant="ghost"
                size="sm"
                className="hidden lg:flex gap-2"
                onClick={() => navigate('/')}
              >
                <Store className="h-4 w-4" />
                <span>Back to Store</span>
              </Button>

              <Button
                variant="ghost"
                size="icon"
                onClick={handleLogout}
                title="Logout"
              >
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {readOnly && <div role="status" className="border-b border-amber-200 bg-[#fff8e9] px-4 py-2 text-center text-xs font-medium text-[#674815]">Read-only access · Contact the Super Admin if you need to change store records.</div>}

      {/* Main Layout */}
      <div className={cn('container mx-auto px-4 py-6', independentScroll && 'flex min-h-0 flex-1 flex-col overflow-hidden')}>
        <div className={cn('flex gap-6', independentScroll && 'min-h-0 flex-1')}>
          {/* Desktop Sidebar */}
          <aside className={cn('admin-navigation hidden lg:block w-64 flex-shrink-0', independentScroll && 'min-h-0')}>
            <div className={cn('sticky top-24 bg-white rounded-lg border border-border p-4 shadow-sm', independentScroll && 'static h-full overflow-y-auto overscroll-contain')} tabIndex={independentScroll ? 0 : undefined} aria-label={independentScroll ? 'Admin navigation' : undefined}>
              <div className="flex flex-col gap-2">
                <NavLinks />
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <main className={cn('flex-1 min-w-0', independentScroll && 'min-h-0 overflow-y-auto overscroll-contain')} tabIndex={independentScroll ? 0 : undefined}>
            {children}
          </main>
        </div>
      </div>
    </div></AdminPermissionProvider></AdminShellContext.Provider>
  );
};
